/**
 * Sprint reads and writes, shared by the backlog planner and the create-issue
 * modal. Sprint dates are serialised by the API as midnight-UTC timestamps
 * (`2026-02-13T00:00:00.000000Z`) but written as bare `yyyy-mm-dd`, so
 * `toDateInput` exists to bridge the two without ever reformatting a value that
 * is on its way back to the server.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import type { UUID } from '@/types/api'

import { v2Api } from './api'
import { issueKeys, issueListKeys } from './keys'
import type { SprintWriteInput } from './types'

export function useSprints(slug: string | null, projectKey: string | null, includeCompleted = true) {
  return useQuery({
    queryKey: issueKeys.sprints(slug ?? '', projectKey ?? ''),
    queryFn: () => v2Api.sprints(slug as string, projectKey as string, includeCompleted),
    enabled: Boolean(slug && projectKey),
    staleTime: 60_000,
    select: (response) => response.data,
  })
}

export function useSprintMutations(slug: string | null, projectKey: string | null) {
  const client = useQueryClient()

  const refresh = () => {
    if (!slug) return
    void client.invalidateQueries({ queryKey: issueKeys.sprints(slug, projectKey ?? '') })
    for (const key of issueListKeys(slug)) {
      void client.invalidateQueries({ queryKey: key })
    }
  }

  const create = useMutation({
    mutationFn: (input: SprintWriteInput) =>
      v2Api.createSprint(slug as string, projectKey as string, input),
    onSuccess: refresh,
  })

  const start = useMutation({
    mutationFn: (sprintId: UUID) => v2Api.startSprint(slug as string, sprintId),
    onSuccess: refresh,
  })

  const complete = useMutation({
    mutationFn: ({ sprintId, moveTo }: { sprintId: UUID; moveTo?: UUID | 'backlog' }) =>
      v2Api.completeSprint(slug as string, sprintId, moveTo),
    onSuccess: refresh,
  })

  const remove = useMutation({
    mutationFn: (sprintId: UUID) => v2Api.deleteSprint(slug as string, sprintId),
    onSuccess: refresh,
  })

  return { create, start, complete, remove }
}

/** `2026-02-13T00:00:00.000000Z` -> `2026-02-13`, for an `<input type=date>`. */
export function toDateInput(value: string | null | undefined): string {
  if (!value) return ''
  return value.slice(0, 10)
}
